/**
 * 中部（信濃・越中・加賀）の国データ
 *
 * 盤面は docs/01-ゲームデザイン.md §2.2 の表に従う。
 * 史実の書きぶりは docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *
 *   20 信濃 … 山国。地の利は弓
 *   23 越中 … 湊の国。地の利は鉄砲（第I章では足軽に読み替え）
 *   24 加賀 … 寺社勢力の国。地の利は僧兵
 *
 * region は令制の道に従う（越中・加賀は 'hokuriku'）が、信濃のみ例外。
 * 信濃は令制では東山道で、Region 型に該当する値がないため 'tokai' としている。
 */

import type { Province } from '../types';

/**
 * 山に囲まれ、盆地ごとに分かれていた国。武田氏と上杉氏が争った。
 *
 * 令制では東山道だが、Region 型に東山道にあたる値がないため、
 * 盤上では甲斐・駿河に続く位置として 'tokai' に置いている（近江と同じ扱い）。
 */
export const SHINANO: Province = {
  id: 20,
  kind: 'province',
  name: '信濃',
  reading: 'しなの',
  // 令制では東山道の国。盤上の区分の都合で 'tokai' としている。gameNote 参照
  region: 'tokai',
  kokudaka: 3,
  defense: 4,
  terrain: 'mountain',
  historicalKokudaka: 41,
  note: {
    summary:
      '山にへだてられ、盆地ごとに小さな勢力が分かれていた広い国。武田氏の侵攻をきっかけに、上杉氏との川中島の戦いが繰り返された。',
    detail: {
      1: 'いまの長野県にあった国です。山が多く、たいらな土地は盆地にしかありません。米はあまりとれませんが、馬や木がたくさんとれました。武田氏と上杉氏が、この国をめぐって川中島で何度も戦いました。',
      2: '現在の長野県にあたる、面積の広い国。周囲を高い山に囲まれ、善光寺平・松本平・伊那谷・佐久平などの盆地に分かれていたため、一人の大名にまとまりにくく、諏訪氏・小笠原氏・村上氏・木曽氏らが並び立っていた。1542年ごろから甲斐の武田氏が侵攻し、追われた国人が越後の上杉氏を頼ったことから、1553年から1564年にかけて川中島で両氏がたびたび対陣した。馬の産地であり、木曽の木材や、善光寺の門前に集まる人と物の流れでも知られる。',
      3: '現在の長野県。約41万石（慶長3年）と石高そのものは大きいが、可耕地は善光寺平・松本平・佐久平・伊那谷などの盆地に分散し、山地で隔てられていたため、国としての一体性は乏しかった。守護小笠原氏の統制力は弱く、諏訪・村上・木曽・仁科などの国人が半ば独立して割拠している。1542年の諏訪頼重滅亡を皮切りに武田晴信（信玄）が北上し、1548年の上田原、1550年のいわゆる砥石崩れでは村上義清に敗れるなど一進一退を経て、1553年に村上氏が越後へ退いた。これを受けて上杉謙信との川中島の対陣が断続的に続く。中山道・北国街道に加え、日本海側と内陸を結ぶ千国街道（塩の道）が通り、塩・海産物と麻・木材・馬が行き交った。善光寺は女性の参詣が認められた寺として広く信仰を集め、その本尊は戦乱のなかで甲斐などへ移され、1598年に信濃へ戻ったと伝えられる。',
    },
    year: [1542, 1582],
    keywords: ['信濃', '川中島', '武田氏', '上杉氏', '善光寺', '木曽'],
    caveat:
      '川中島の戦いは5回とされますが、回数の数え方や各回の規模・経過は史料によって差があり、詳細の多くは『甲陽軍鑑』など後世の編纂物に頼っています。上杉謙信が武田方へ塩を送ったという「敵に塩を送る」の逸話も、同時代の史料では確認できず、後世に広まった話とされます。',
    gameNote:
      '盤上の石高ランク3と防御4は遊びやすさのための相対値です。実際の信濃は慶長3年の集計で約40.8万石で、石高としては当時の上位に入ります。地形を「山国」としているため、合戦では戦場札2枚が弓になります。なお信濃は令制では東山道の国ですが、盤上では甲斐・駿河に続く位置に置いているため、区分の都合で東海（tokai）として扱っています。',
  },
};

/** 急流と湊の国。守護代・一向一揆・上杉・織田がめまぐるしく入れ替わった。 */
export const ECCHU: Province = {
  id: 23,
  kind: 'province',
  name: '越中',
  reading: 'えっちゅう',
  region: 'hokuriku',
  kokudaka: 2,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 38,
  note: {
    summary:
      '急な川がいくつも流れる平野と、日本海に開いた湊を持つ国。守護代・一向一揆・越後の上杉氏・織田方が入れ替わり支配した。',
    detail: {
      1: 'いまの富山県にあった国です。山からいきおいよく流れる川が多く、こう水がよく起こりました。日本海に面した港があり、船で物を運んでさかえました。となりの国から何度も軍がせめこんできた国でもあります。',
      2: '現在の富山県にあたる国。常願寺川・神通川・庄川・黒部川といった勾配の急な川が流れ、たびたび氾濫して田畑を流したため、堤を築く工事が繰り返された。放生津や伏木などの湊があり、日本海の海運で畿内と結ばれていた。守護は畠山氏だが京にいることが多く、守護代の神保氏と椎名氏が争い、そこへ瑞泉寺・勝興寺を中心とする一向一揆の勢力も加わって、国内は長く分裂した。のちに越後の上杉氏が進出し、上杉謙信の死後は織田方の佐々成政が支配したが、1585年に羽柴（豊臣）秀吉に降った。',
      3: '現在の富山県。慶長3年の集計で約38万石と、北陸では有数の石高を持つ。立山連峰から富山湾までの距離が短く、常願寺川・神通川・庄川・黒部川はいずれも勾配が急で、洪水と流路の変化が農業の最大の制約だった。堤防の普請は領主と村の共同事業として繰り返され、江戸期の加賀藩による大規模な治水へ引き継がれる。守護畠山氏の在京により、射水・婦負郡を神保氏、新川郡を椎名氏が実質的に治める分割状態が生じ、そこに越中一向一揆（1481年の田屋川原の戦いなど）が加わって抗争が続いた。1560年代以降は上杉謙信が繰り返し越中へ出兵し、1578年の謙信の死後は織田方の佐々成政が国内を押さえたが、1585年の秀吉の出兵により降伏する。信仰の面では立山が知られ、麓の芦峅寺・岩峅寺の宿坊が登拝者を受け入れた。山上は女人禁制であり、登れない女性のために布橋灌頂会という儀礼が行われたことが知られるが、その具体的な次第がわかるのは主に江戸時代の史料である。',
    },
    year: [1467, 1585],
    keywords: ['越中', '神保氏', '椎名氏', '一向一揆', '立山', '治水'],
    caveat:
      '神保氏・椎名氏の支配範囲は年ごとに変動し、史料も断片的なため確定していません。立山信仰の布橋灌頂会など儀礼の内容が具体的にわかるのは主に江戸時代の記録で、戦国期の姿をそのまま遡ることはできません。越中売薬（富山の薬売り）も江戸時代に発達したもので、戦国期の産業ではありません。',
    gameNote:
      '盤上の石高ランク2は、隣接する越後・加賀との差をつけるための相対値です。実際の越中は慶長3年の集計で約38.0万石あり、決して小さな国ではありません。地形を「湊」としているため、合戦では戦場札2枚が鉄砲（第I章では足軽に読み替え）になります。',
  },
};

/** 手取川の扇状地に開けた米どころ。1488年から約90年、本願寺の影響下にあった。 */
export const KAGA: Province = {
  id: 24,
  kind: 'province',
  name: '加賀',
  reading: 'かが',
  region: 'hokuriku',
  kokudaka: 2,
  defense: 3,
  terrain: 'temple',
  historicalKokudaka: 36,
  note: {
    summary:
      '手取川の扇状地に開けた北陸有数の米どころ。1488年に守護が倒され、以後およそ90年にわたり本願寺の影響下で寺院・門徒・国人による支配が続いた。',
    detail: {
      1: 'いまの石川県の南がわにあった国です。手取川がはこんだ土で平地がひろがり、米がよくとれました。1488年、浄土真宗を信じる人びとが守護をたおし、そのあと約90年のあいだ、お寺を中心とした支配がつづきました。',
      2: '現在の石川県南部にあたる国。手取川の扇状地に平野が開け、北陸でも有数の米どころだった。15世紀に浄土真宗（一向宗）が広まり、1488年（長享2年）に門徒と国人が守護の富樫政親を高尾城に攻めて自害させた。以後およそ90年にわたり、本願寺の影響下で寺院・門徒・国人の合議による支配が続き、後世これを「百姓の持ちたる国」と呼んだ。門徒は講という集まりを単位に結びつき、村の生活そのものが信仰の組織と重なっていた。1580年に織田方の柴田勝家らが金沢御堂を攻め落とし、1583年からは前田利家が金沢を本拠とした。',
      3: '現在の石川県南部。手取川扇状地の水利に恵まれ、慶長3年の集計で約35.6万石を数える。15世紀後半、蓮如が越前吉崎に坊舎を構えて（1471年）北陸へ布教した影響で浄土真宗が急速に広まり、講を単位とする門徒の組織が村落に定着した。1488年（長享2年）、加重な負担をめぐる対立から門徒と国人が守護富樫政親を高尾城に攻め、政親は自害する。以後、名目上は富樫泰高が守護に立てられつつ、実際には本願寺の坊官・有力寺院・国人層による重層的な支配が続いた。1546年には金沢御堂（尾山御坊）が造営され、宗教と政治の中心となる。1577年の手取川の戦いを経て、1580年に織田方の柴田勝家・佐久間盛政が金沢御堂を攻略し、1583年に前田利家が入って以後は加賀藩の中心となった。なお「百姓ノ持タル国」は本願寺関係者の記した『実悟記拾遺』の表現であり、近年の研究では、農民の自治というより寺院・門徒・国人を含む複合的な支配体制と捉えられている。',
    },
    year: [1488, 1583],
    keywords: ['加賀', '一向一揆', '本願寺', '富樫氏', '手取川', '金沢御堂'],
    caveat:
      '「百姓の持ちたる国」という言い方は後世に定着したもので、実態は本願寺の坊官・有力寺院・国人層による支配であったとする見直しが進んでいます。名目上の守護として富樫泰高が立てられており、武士の支配が消えたわけではありません。また一揆勢の人数（20万など）は後世の記録によるもので、実数はわかっていません。',
    gameNote:
      '盤上の石高ランク2は遊びやすさのための相対値です。実際の加賀は慶長3年の集計で約35.6万石でした。地形を「寺社領」としているため合戦では戦場札2枚が僧兵になりますが、これは寺院勢力の強さを遊びに落とし込んだ便宜的な表現です。加賀一向一揆の担い手は僧兵ではなく、門徒となった百姓や国人でした。',
  },
};
